<script setup>
import IconCheck from 'assets/images/svg/check.svg'
import ArrowIcon from 'assets/images/svg/btn-arrow.svg'

import { Navigation, Pagination, A11y } from 'swiper/modules';

import { Swiper, SwiperSlide } from 'swiper/vue';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

const modules = [Navigation, Pagination, A11y];

const whoFits = await queryContent('/who-fits').findOne()
</script>

<template class="who-fits">
  <section class="who-fits">
    <div class="container">
      <div class="who-fits__header">
        <h2 class="who-fits__title text-40">{{ whoFits.title }}</h2>
        <div class="btn-slider">
          <button type="button" class="btn-slider-prev">
            <ArrowIcon class="btn-slider__arrow" />
          </button>
          <button type="button" class="btn-slider-next">
            <ArrowIcon class="btn-slider__arrow" />
          </button>
        </div>
      </div>
      <Swiper
        :modules="modules"
        :slides-per-view="3"
        :space-between="15"
        :pagination="{ clickable: true }"
        :watch-overflow="true"
        :navigation="{ nextEl: '.btn-slider-next', prevEl: '.btn-slider-prev' }"
        :breakpoints="{
          1280: { slidesPerView: 3 },
          768: { slidesPerView: 2.2 },
          200: { slidesPerView: 1.08 },
        }"
        class="who-fits__list"
      >
        <SwiperSlide v-for="(item, index) in whoFits.list" :key="index" class="who-fits__list-item">
          <IconCheck class="who-fits__check" />
          <NuxtImg 
            v-if="index === 0" 
            class="who-fits__wheel" 
            src="/img/who-fits/steering-wheel.png" 
            loading="lazy"
            format="webp"
            alt="Руль"
          />
          <div v-if="index === 1" class="who-fits__map">
            <NuxtImg 
              class="who-fits__map-marker" 
              src="/img/who-fits/marker.png" 
              loading="lazy"
              format="webp"
              alt="Маркер"
            />
            <div class="who-fits__map-network">
              <ul>
                <li /><li /><li /><li /><li /><li />
                <li /><li /><li /><li /><li /><li />
                <li /><li /><li /><li /><li /><li />
                <li /><li /><li /><li /><li /><li />
                <li /><li /><li />
              </ul>
            </div>
          </div>
          <div v-if="index === 2" class="who-fits__car-wheel">
            <NuxtImg 
              class="who-fits__car-wheel-img" 
              src="/img/who-fits/car-wheel.png" 
              loading="lazy" 
              format="webp"
              alt="Колесо"
            />
            <NuxtImg 
              class="who-fits__brake-disc-img" 
              src="/img/who-fits/brake-disc.png" 
              loading="lazy" 
              format="webp"
              alt="Диск"
            />
          </div>
          <h3 class="who-fits__item-title text-22">{{ item.title }}</h3>
          <p class="who-fits__description text-20">
            {{ item.description }}
          </p>
        </SwiperSlide>
      </Swiper>
    </div>
  </section>
</template>

<style lang="scss">
.who-fits {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  background-color: var(--bg-grey);
  padding-top: 56px;
  padding-bottom: 68px;
  overflow: hidden;

  @media (max-width: $large) {
    padding-bottom: 56px;
  }

  @media (max-width: $medium) {
    padding-bottom: 83px;
  }

  @media (max-width: $small) {
    padding-top: 36px;
    padding-bottom: 83px;
  }

  .swiper-pagination {
    bottom: -50px;

    .swiper-pagination-bullet-active {
      background: var(--info-color);
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 101px;

    @media (max-width: $large) {
      margin-bottom: 56px;
    }

    @media (max-width: $medium) {
      margin-bottom: 36px;
    }
  }

  &__car-wheel {
    position: absolute;
    top: -32px;
    right: 32px;

    @media (max-width: $large) {
      width: 148px;
      height: 148px;
      top: -16px;
      right: 24px;
    }

    @media (max-width: $medium) {
      width: 130px;
      height: 130px;
    }

    @media (max-width: $small) {
      width: 120px;
      height: 120px;
    }

    .who-fits__car-wheel-img {
      position: relative;
      z-index: 1;
      width: 100%;
    }

    .who-fits__brake-disc-img {
      position: absolute;
      width: 113px;
      height: 113px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      @media (max-width: $large) {
        width: 90px;
        height: 90px;
      }
    }
  }

  &__map {
    width: 222px;
    height: 154px;
    border: 8px solid #3c3c3c;
    border-radius: 22px;
    position: absolute;
    top: -20px;
    right: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #e5e5e5;
    overflow: hidden;
    box-shadow: -15px 22px 30px -5px rgba(34, 60, 80, 0.2);

    @media (max-width: $large) {
      width: 180px;
      height: 123px;
      top: -12px;
      right: 24px;
    }

    @media (max-width: $medium) {
      width: 158px;
      height: 108px;
      border: 6px solid #3c3c3c;
    }

    &-marker {
      width: 73px;
      height: 82px;
      position: relative;
      right: 10px;
      z-index: 1;

      @media (max-width: $large) {
        width: 57px;
        height: 64px;
      }
    }

    &-network {
      position: absolute;
      bottom: -160px;
      width: 500px;
      height: auto;

      ul {
        li {
          margin-bottom: 40px;

          &:nth-child(odd) {
            width: 100%;
            height: 20px;
            background-color: white;
            transform: rotate(-45deg);
          }

          &:nth-child(even) {
            width: 100%;
            height: 20px;
            background-color: white;
            transform: rotate(45deg);
          }
        }
      }
    }
  }

  &__title {
    color: var(--dark-color);
  }

  &__icon {
    width: 50px;
    height: 50px;
    margin: 0 auto 15px;
  }

  &__check {
    width: 64px;
    height: 64px;
    stroke: var(--dark-color);

    @media (max-width: $large) {
      width: 56px;
      height: 56px;
    }

    @media (max-width: $medium) {
      width: 48px;
      height: 48px;
    }
  }

  &__item-title {
    color: var(--dark-color);
    margin-top: 78px;
    margin-bottom: 50px;
    max-width: 340px;

    @media (max-width: $large) {
      margin-top: 68px;
      margin-bottom: 48px;
    }

    @media (max-width: $medium) {
      margin-top: 64px;
      margin-bottom: 44px;
    }

    @media (max-width: $small) {
      margin-top: 56px;
      margin-bottom: 36px;
      font-size: 18px;
    }
  }

  &__description {
    color: var(--dark-color);

    @media (max-width: $medium) {
      font-size: 16px;
    }
  }

  &__wheel {
    position: absolute;
    top: -34px;
    right: 34px;
    filter: drop-shadow(-11px 4px 18px rgba(0, 0, 0, 0.2));

    @media (max-width: $large) {
      width: 148px;
      height: 148px;
      top: -16px;
      right: 24px;
    }

    @media (max-width: $medium) {
      width: 130px;
      height: 130px;
    }
    @media (max-width: $medium) {
      width: 120px;
      height: 120px;
    }
  }

  &__list {
    display: flex;
    flex: 1;
    gap: 20px;
    overflow: visible;

    &-item {
      border-radius: 87px;
      padding: 64px 72px 121px 72px;
      position: relative;
      height: auto;

      @include transition();

      @media (max-width: $large) {
        padding: 48px 40px 76px 40px;
        border-radius: 36px;
      }

      @media (max-width: $medium) {
        padding: 24px 24px 74px 24px;
      }

      @media (max-width: $small) {
        padding: 24px 24px 36px 24px;
      }

      &:hover {
        border-radius: 30px;
      }

      &:nth-child(1) {
        background-color: rgba(127, 116, 255, 0.1);

        &:hover {
          .who-fits__wheel {
            animation: rotate-wheel 3s infinite;
          }
        }
      }

      &:nth-child(2) {
        background-color: rgba(116, 155, 255, 0.1);

        &:hover {
          .who-fits__map-network {
            animation: slideDown infinite 20s linear;
          }
        }
      }

      &:nth-child(3) {
        background-color: rgba(117, 204, 194, 0.2);

        &:hover {
          .who-fits__car-wheel-img {
            animation: rotate-car-wheel infinite 1s linear;
          }
        }
      }
    }
  }

  &__arrows {
    display: flex;
    gap: 10px;
    align-items: center;

    &-button {
      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      background: #e0e0e0;
      cursor: pointer;
      font-size: 18px;
      color: #222;
      border: none;
      transition: all 0.3s;

      &:hover {
        background: #d0d0d0;
        transform: scale(1.1);
      }
    }
  }
}

@keyframes rotate-wheel {
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(40deg);
  }
  50% {
    transform: rotate(-40deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@keyframes slideDown {
  0% {
    transform: translateY(0%);
  }
  100% {
    transform: translateY(100%);
  }
}

@keyframes rotate-car-wheel {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
